/**
 * The deactivate confirmation (f4.1), driven entirely by the server.
 *
 * The prototype's `modal narrow` with `alert alert-warning` carrying `a-title`
 * and `a-body`, plus its Reason textarea.
 *
 * **The client never computes the child list.** The sequence is exactly the
 * one the specification fixes:
 *
 * 1. The dialog opens as a plain confirmation -- nothing is known yet, and a
 *    deactivation is destructive enough to ask about on its own.
 * 2. Confirming issues the deactivation **without** `acknowledge_active_children`.
 * 3. If the server refuses with `409 ACTIVE_CHILDREN_PRESENT`, the dialog stays
 *    open and renders the children **it** named.
 * 4. Confirming again re-issues the same request with the acknowledgement.
 *
 * Step 2 is what makes the warning trustworthy: it is the server's own answer
 * to the real request, so the dialog cannot disagree with the rule that
 * produced it -- and it cannot go stale between the two, because a child
 * created in between makes step 4 refuse again rather than succeed silently.
 */

import { useState } from 'react';
import { Ban, TriangleAlert } from 'lucide-react';

import type { BusinessUnitGroup } from '@/api/client';
import { useDeactivateBusinessUnitGroup } from '@/api/queries';
import { Modal } from '@/routes/reference-data/Modal';

import { activeChildrenFrom, generalMessage } from './groupView';

export function DeactivateGroupDialog({
  group,
  onClose,
}: {
  group: BusinessUnitGroup;
  onClose: () => void;
}) {
  const deactivate = useDeactivateBusinessUnitGroup();
  const [reason, setReason] = useState('');

  const activeChildren = activeChildrenFrom(deactivate.error);
  // A refusal that named children is the expected answer to step 2, not a
  // failure to repeat at the top of the dialog.
  const failure = activeChildren ? null : generalMessage(deactivate.error);

  function submit() {
    deactivate.mutate(
      {
        groupId: group.id,
        body: {
          // False on the first attempt, true once the warning has been seen.
          acknowledge_active_children: activeChildren !== null,
          ...(reason.trim() ? { reason: reason.trim() } : {}),
        },
      },
      { onSuccess: onClose },
    );
  }

  return (
    <Modal
      title="Deactivate group?"
      subtitle={group.name}
      onClose={onClose}
      onSubmit={submit}
      busy={deactivate.isPending}
      submitLabel={activeChildren ? 'Deactivate anyway' : 'Deactivate'}
      submitIcon={<Ban aria-hidden="true" />}
      submitClassName="btn btn-danger"
      narrow
      layout="plain"
      message={failure}
    >
      {activeChildren && activeChildren.total > 0 && (
        <div className="alert alert-warning mb-4" role="alert">
          <TriangleAlert aria-hidden="true" />
          <div>
            <div className="a-title">
              {activeChildren.total}{' '}
              {activeChildren.total === 1 ? 'record is' : 'records are'} still active
              under this group
            </div>
            <div className="a-body">
              They stay active and keep appearing in the open cycle. Nothing is deleted:
              the group is hidden from new selections and can be reactivated.
            </div>
            <ul className="group-children">
              {activeChildren.listed.map((entry) => (
                <li key={entry.id}>
                  <span className="mono">{entry.code}</span> {entry.name}
                </li>
              ))}
            </ul>
            {activeChildren.total > activeChildren.listed.length && (
              <div className="a-body">
                and {activeChildren.total - activeChildren.listed.length} more.
              </div>
            )}
          </div>
        </div>
      )}

      <div className="field">
        <label htmlFor="bug-deact-reason">Reason</label>
        <textarea
          id="bug-deact-reason"
          value={reason}
          placeholder="Recorded on the audit entry"
          onChange={(event) => setReason(event.target.value)}
        />
      </div>
    </Modal>
  );
}
